// Sad lofi (docs/AUDIO_DESIGN.md §12): a rainy-night felt piano, soft and far away on a warbling bus, holding minor chords
// (the line cliché, falling bass lines, the borrowed iv; major keys centre on vi) over a slow string pad and a round sub,
// a hushed kit with many rests (some sections drop it), and a lonely muted trumpet that sighs down by step.
// Instruments are in sad-voices.js (the piano itself is piano-instruments.js createPiano, played darker and softer).
import { QUALITIES, bassNote } from '../harmony.js';
import { createSadKeys, createSadSub, createHushKit, createMuteHorn, stepAbove } from './sad-voices.js';

const mod12 = n => ((n % 12) + 12) % 12;
const avg = a => a.reduce((s, x) => s + x, 0) / a.length;

/* ---------------- voicing: the left hand's root + a low, close right hand whose top line would rather fall ---------------- */
// Right-hand tones per quality (semitones over the root): ♭3 5 ♭7 9 on m9, ♭3 ♭7 9 11 on m11, ♭3 5 R 9 on madd9 (so the
// cliché's line starts on the root), ♭3 5 7 9 / ♭3 5 6 9 on sadmmaj9 / m6, a plain ♭3 5 ♭7 on m7 (the minor v keeps
// no 9th), 3 5 7 9 on maj7, 3 5 R 9 on add9, 3 5 6 R on 6 (♭VII6 = v7 over ♭7), 4 5 ♭7 R on 7sus4, 5 R 9 5 on sadsus2.
const RH = {
  m9: [3, 7, 10, 14], m11: [3, 10, 14, 17], madd9: [3, 7, 12, 14], sadmmaj9: [3, 7, 11, 14], m6: [3, 7, 9, 14], m7: [3, 7, 10],
  m7b5: [3, 6, 10, 17], maj7: [4, 7, 11, 14], maj9: [4, 7, 11, 14], add9: [4, 7, 12, 14], 6: [4, 7, 9, 12], 69: [4, 7, 9, 14],
  '7sus4': [5, 7, 10, 12], '9sus4': [5, 7, 10, 14], sadsus2: [7, 12, 14, 19], 7: [4, 7, 10, 12],
};
const rhTones = q => {
  if (RH[q]) return RH[q];
  let iv = [...new Set((QUALITIES[q] || QUALITIES.m9).filter(i => i % 12))];
  if (iv.length > 4) iv = iv.filter(i => i !== 7).slice(0, 4);
  return iv.length ? iv : [3, 7];
};
const perms = n => (n <= 1 ? [[0]] : perms(n - 1).flatMap(p => Array.from({ length: n }, (_, k) => [...p.slice(0, k), n - 1, ...p.slice(k)])));
const PERMS = [null, perms(1), perms(2), perms(3), perms(4)];
const CANDS = new Map();
/** Right-hand candidates: each order of the tones stacked upwards from a lowest note in MIDI 50–62, kept when the top is
 *  60–72 and the span 5–19, with neighbours ≥ a whole step apart (≥ a minor 3rd below MIDI 55) and no minor 9th. The first
 *  chord's window is widened by the track's register (e.g. −5: lowest 45–62, top 55–72). Cached. */
function rhCandidates(pcs, reg = 0) {
  const id = `${pcs.join(',')}:${reg}`, lo0 = 50 + Math.min(0, reg), lo1 = 62 + Math.max(0, reg), t0 = 60 + Math.min(0, reg), t1 = 72 + Math.max(0, reg);
  if (CANDS.has(id)) return CANDS.get(id);
  const out = [];
  for (const order of PERMS[pcs.length]) for (let lo = lo0; lo <= lo1; lo++) {
    if (mod12(lo) !== pcs[order[0]]) continue;
    const v = [lo];
    for (let k = 1; k < order.length; k++) { let m = v[k - 1] + 1; while (mod12(m) !== pcs[order[k]]) m++; v.push(m); }
    const top = v[v.length - 1], span = top - lo;
    if (top < t0 || top > t1 || span < 5 || span > 19) continue;
    let ok = true;
    for (let k = 1; k < v.length && ok; k++) if (v[k] - v[k - 1] < 2 || (v[k - 1] < 55 && v[k] - v[k - 1] < 3)) ok = false;
    for (let a = 0; a < v.length && ok; a++) for (let c = a + 1; c < v.length; c++) if (v[c] - v[a] === 13) ok = false;
    if (ok && !out.some(w => w.join() === v.join())) out.push(v);
  }
  CANDS.set(id, out);
  return out;
}
/** Cost against the previous right hand (voices paired from the top): Σ|Δ| + 1.5·max(0, |Δtop| − 2) (+4 past a 4th) − .6 per
 *  common tone, the top +.8 when it climbs and −.8 when it falls a step (the line sighs down until the register pulls it
 *  back), + .35·|mean − 60| + .6·max(0, top − 69) + .15·|span − 12| + 2 per semitone the right hand sits closer than a 5th
 *  over the left hand. The first chord: the register terms and .3·|top − 67|, all moved by the track's register (−5..+5
 *  semitones, plan.register), so consecutive tracks don't open on the same top note (the later chords lead on from it). */
function rhCost(v, prev, lh, reg = 0) {
  const top = v[v.length - 1], home = prev ? 0 : reg;
  let c = 0.35 * Math.abs(avg(v) - 60 - home) + 0.6 * Math.max(0, top - 69 - home) + 0.15 * Math.abs(top - v[0] - 12) + 2 * Math.max(0, lh + 7 - v[0]);
  if (!prev) return c + 0.3 * Math.abs(top - 67 - reg);
  for (let i = 1; i <= Math.min(v.length, prev.length); i++) c += Math.abs(v[v.length - i] - prev[prev.length - i]);
  const dt = top - prev[prev.length - 1];
  c += 1.5 * Math.max(0, Math.abs(dt) - 2) + (Math.abs(dt) > 5 ? 4 : 0) + (dt > 0 ? 0.8 : dt >= -2 && dt < 0 ? -0.8 : 0);
  for (const m of v) if (prev.includes(m)) c -= 0.6;
  return c;
}
/** the left hand's root: MIDI 36–48, nearest the previous one, leaning to 40 */
const lhRoot = (pc, prev = 40) => {
  let best = 36 + mod12(pc - 36), bc = Infinity;
  for (let m = best; m <= 48; m += 12) { const c = Math.abs(m - prev) + 0.4 * Math.abs(m - 40); if (c < bc) { bc = c; best = m; } }
  return best;
};
/** [left-hand root, ...right hand low → high]; the same chord again keeps its voicing (re-strikes and 2-bar chords). The
 *  first chord aims at the home register moved by plan.register (the left hand starts from 40 + register too). */
function voiceSad(sp, prev, chord, plan) {
  const root = mod12(plan.key.tonic + chord.root), pcs = rhTones(chord.q).map(i => mod12(root + i)), set = new Set(pcs);
  if (prev && mod12(prev[0]) === root && prev.length === pcs.length + 1 && prev.slice(1).every(m => set.has(mod12(m)))
    && pcs.every(pc => prev.slice(1).some(m => mod12(m) === pc))) return prev.slice();
  const reg = plan.register || 0, lh = lhRoot(root, prev ? prev[0] : 40 + reg), pr = prev ? prev.slice(1) : null;
  let best = null, bc = Infinity;
  for (const v of rhCandidates(pcs, prev ? 0 : reg)) { const c = rhCost(v, pr, lh, reg); if (c < bc) { bc = c; best = v; } }
  if (!best) { best = []; for (const pc of pcs) { let m = 53 + mod12(pc - 53); while (best.length && m <= best[best.length - 1]) m += 12; best.push(m); } }
  return [lh, ...best];
}

/* ---------------- comping: mostly held; every note rings to the bar's end (the engine's cut at the chord end = the pedal) ---------------- */
// cells of [step, length, velocity, pick]; pick indices into the voicing: 0 = the left hand's root, 1.. the right hand, −1 the top
const cell = (s, v = 1, pick = null) => (pick ? [s, 16 - s, v, pick] : [s, 16 - s, v]);
const RHAND = [1, 2, 3, 4];
const COMPS = {
  hold: [cell(0)],                                                                  // a held chord (often rolled)
  still: [cell(0), cell(10, 0.55, [-1])],                                           // held, its top touched again on the "and" of 3
  late: [cell(0, 1, [0]), cell(3, 0.85, RHAND)],                                    // the root, then the right hand a dotted 8th late
  fall: [cell(0), cell(4, 0.5, [-1]), cell(8, 0.5, [-2]), cell(12, 0.48, [-3])],    // held, its upper notes touched again high → low
  two: [cell(0), cell(8, 0.7)],                                                     // on 1 and 3
};

/* ---------------- drums: a hushed kit, many rests ---------------- */
// K kick · S brush · R muted cross-stick · H hat; s = soft (×.55)
const GRIDS = {
  hush: { K: 'x...............', R: '........x.......' },                                        // kick on 1, a rim on 3
  slow: { K: 'x.........x.....', S: '....x.......x...', H: '..s.......s.....' },
  limp: { K: 'x.....x...x.....', R: '....x.......x...', H: '..s...s...s...s.' },
  heart: { K: 'xs........x.....', S: '........x.......', H: 's...s...s...s...' },                // a heartbeat kick, the brush on 3
  rain: { K: 'x......x..x.....', S: '....x.......x...', H: 's.s.s.s.s.s.s.s.' },
  break: { K: 'x...............', H: '........s.......' },
};
const GROOVES = {
  chill: { hush: 2, slow: 1.2, heart: 1, limp: 0.6 },
  balanced: { slow: 1.4, limp: 1, heart: 1, hush: 0.6, rain: 0.4 },
  upbeat: { slow: 1.2, rain: 1, limp: 1, heart: 0.8 },
  fast: { rain: 1.3, slow: 1, limp: 0.8, heart: 0.6 },
};

/* ---------------- plan: timbres, bare sections, the lonely lead's motif ---------------- */
// 2-bar motif cells [steps, durations] (16ths; the last note long) and falling scale-step contours by length: sighs
const SIGHS = [
  [[0, 6], [6, 14]], [[0, 8, 12], [8, 4, 14]], [[2, 8, 16], [6, 8, 12]], [[0, 4, 16], [4, 12, 12]],
  [[0, 10, 16, 22], [10, 6, 6, 10]], [[4, 8, 20], [4, 12, 10]],
];
const FALLS = { 2: [[0, -1], [1, 0], [1, -1]], 3: [[0, -1, -2], [1, 0, -1], [2, 1, 0], [1, 0, -2]], 4: [[1, 0, -1, -2], [2, 1, 0, -1], [0, -1, -2, -1]] };
function plan(p, { r, E }) {
  const rr = (a, b, d = 2) => +(a + r() * (b - a)).toFixed(d);
  p.piano = { detune: rr(1.6, 3), stretch: rr(0.8, 1.2), hammer: rr(0.45, 0.75), bright: rr(0.72, 0.9), roll: rr(...E.roll) };
  p.sad = {
    warble: rr(...E.warble, 1), rate: rr(0.32, 0.5), pad: rr(0.8, 1.2), padAtk: rr(0.5, 0.8), padRel: rr(0.7, 1), det: rr(6, 11, 1),
    padLp: rr(1000, 1500, 0), walk: rr(0.2, 0.5), lean: rr(...E.lean), vib: rr(10, 16, 1),
  };
  p.drums.kit = {
    kickF0: rr(95, 110, 1), kickF1: rr(40, 46, 1), kickDecay: rr(0.2, 0.28, 3), snareBP: rr(1100, 1600, 0), snareDecay: rr(0.12, 0.2, 3),
    hatHP: rr(3600, 4800, 0), hatClosed: rr(0.018, 0.03, 3), lp: p.drums.kit.lp,
  };
  // bare sections: the first A (and now and then a later A or B) drop the kit as a section layer, so 'no drums' still
  // equals the full band minus the drums; a 16-bar section at half the odds (a minute and more without a beat is Ambient)
  let firstA = true;
  for (const s of p.sections) {
    if (s.name !== 'A' && s.name !== 'B') continue;
    if (r() < (s.name === 'A' && firstA ? E.bareFirst : E.bare) * Math.min(1, 8 / s.bars)) s.layers.drums = 'none';
    if (s.name === 'A') firstA = false;
  }
  if (p.lead) {
    const [steps, durs] = SIGHS[Math.floor(r() * SIGHS.length)], falls = FALLS[steps.length];
    p.lead.timbre = 'mute';
    p.lead.motif = { ...p.lead.motif, steps, durs, contour: falls[Math.floor(r() * falls.length)], vel: steps.map((s, i) => (i ? rr(0.56, 0.64) : 0.72)) };
  }
}

/* ---------------- bass: long soft roots, walking down between falling chords ---------------- */
/** A root per chord held to its end (the octave nearest the last, so falling progressions step down); in A/B, when the
 *  next bar's root lies a 3rd to a 5th below, the line walks there through the scale step above it on beat 4 (p walk). */
function bass({ plan: p, st, b, mode, chords, nextChords, stop }) {
  const out = [], end = stop ?? 16, K = p.key;
  chords.forEach((c, k) => {
    const s = c.start * 4;
    if (s >= end) return;
    const m = bassNote(mod12(K.tonic + c.root), st.prevBass);
    st.prevBass = m;
    if (c.final) { out.push({ s, midi: m, vel: 0.8, dur: (b.n - 2) * 16 }); return; }
    const e = Math.min(end, s + c.beats * 4), vel = c.onset ? 0.84 : 0.72, nx = k === chords.length - 1 && nextChords ? nextChords[0] : null;
    if (mode === 'kick' && nx && e === 16 && e - s >= 8) {
      const tgt = bassNote(mod12(K.tonic + nx.root), m), gap = m - tgt;
      if (gap >= 3 && gap <= 7 && st.rS() < p.sad.walk) {
        out.push({ s, midi: m, vel, dur: 12 - s }, { s: 12, midi: stepAbove(tgt, K), vel: 0.64, dur: 4 });
        return;
      }
    }
    out.push({ s, midi: m, vel, dur: e - s });
  });
  return out;
}

export default {
  id: 'sad', name: 'Sad', desc: 'Minor chords, slow and sparse, with a lonely melody',
  //            chill                        balanced                     upbeat
  vary: 'colour',
  energy: {
    stretch:    [0.6,                        0.45,                        0.3],              // odds of 2-bar chords
    bpm:        [[56, 64],                   [60, 70],                    [66, 76]],         // 56: the 54-bar forms stay ≤ 240 s
    swing:      [[57, 62],                   [56, 61],                    [55, 59]],
    snareLag:   [[0.014, 0.026],             [0.01, 0.02],                [0.006, 0.014]],
    groove:     [[GROOVES.chill, GROOVES.chill], [GROOVES.balanced, GROOVES.balanced], [GROOVES.upbeat, GROOVES.fast]],
    drumLevel:  [0.6,                        0.66,                        0.74],
    kitLp:      [[2600, 3600],               [3000, 4200],                [3400, 4800]],
    epLp:       [[1500, 2200],               [1700, 2600],                [2000, 3000]],     // the piano's felt LP: darker than Piano's
    strum:      [[0.02, 0.05],               [0.015, 0.04],               [0.012, 0.03]],
    epVel:      [[0.36, 0.46],               [0.38, 0.48],                [0.42, 0.52]],
    comp: [
      { hold: [1, 1.5], still: [0.3, 0.7], late: [0.2, 0.5], fall: [0.1, 0.4], two: [0, 0.05] },
      { hold: [0.6, 1.1], still: [0.3, 0.7], late: [0.2, 0.6], fall: [0.2, 0.6], two: [0.05, 0.25] },
      { hold: [0.3, 0.7], still: [0.3, 0.6], late: [0.2, 0.5], fall: [0.3, 0.7], two: [0.2, 0.5] },
    ],
    push:       [0.03,                       0.06,                        0.1],
    lead:       [0.7,                        0.75,                        0.8],
    tone:       [[3400, 5200],               [4000, 6000],                [4600, 6800]],
    wowCents:   [[8, 13],                    [7, 11],                     [5, 9]],
    dust:       [[1, 1.7],                   [0.9, 1.5],                  [0.7, 1.3]],
    introHats:  [0.1,                        0.2,                         0.35],
    leadFrom:   [2,                          2,                           1],
    hatsFirst:  [0.3,                        0.25,                        0.2],
    breakDrums: [0.1,                        0.25,                        0.45],
    breakLead:  [0.45,                       0.5,                         0.55],
    roll:       [[0.35, 0.6],                [0.3, 0.5],                  [0.2, 0.4]],       // odds a block chord is rolled
    warble:     [[4, 7],                     [3, 6],                      [2, 5]],           // ¢ of pitch drift on the keys bus
    lean:       [[0.35, 0.55],               [0.3, 0.5],                  [0.25, 0.45]],     // odds a long lead note sighs in from above
    bareFirst:  [0.6,                        0.4,                         0.2],              // odds the first A has no drums
    bare:       [0.15,                       0.08,                        0.04],             // … a later A or B
    // development (arrange.js BY_ENERGY): no lift; rarely a solo; alone or the lonely lead first
    lift:       [0, 0, 0],
    solo:       [0.1, 0.15, 0.2],
    intro:      [{ keys: 0.45, beat: 0.1, groove: 0, lead: 0.35, cold: 0.1 }, { keys: 0.45, beat: 0.1, groove: 0, lead: 0.35, cold: 0.1 }, { keys: 0.45, beat: 0.1, groove: 0, lead: 0.35, cold: 0.1 }],
  },
  qualities: { sadmmaj9: [0, 3, 7, 11, 14], sadsus2: [0, 2, 7] },
  progressions: {
    major: {                                                                                   // centred on vi
      S1: [[9, 'm9', 4], [5, 'maj7', 4], [0, 'add9', 4], [7, '9sus4', 4]],                     // vi–IV–I–V
      S2: [[9, 'm9', 4], [4, 'm7', 4], [5, 'maj7', 4], [5, 'm6', 4]],                          // vi–iii–IV–iv
      S3: [[5, 'maj9', 4], [5, 'm6', 4], [0, 'add9', 4], [9, 'm7', 4]],                        // IV–iv–I–vi
      S4: [[9, 'm9', 4], [7, '6', 4], [5, 'maj7', 4], [4, 'm7', 4]],                           // vi–V6–IV–iii (bass 6 5 4 3)
      S5: [[9, 'madd9', 4], [9, 'sadmmaj9', 4], [9, 'm9', 4], [5, 'maj7', 4]],                 // vi line cliché → IV
      S6: [[9, 'm11', 4], [0, 'add9', 4], [5, 'maj7', 4], [5, 'm6', 4]],                       // vi–I–IV–iv
      S7: [[2, 'm9', 4], [4, 'm7', 4], [9, 'm9', 8]],                                          // ii–iii–vi
    },
    minor: {
      s1: [[0, 'm9', 4], [8, 'maj7', 4], [3, 'add9', 4], [10, 'sadsus2', 4]],                  // i–VI–III–VII
      s2: [[0, 'm11', 4], [5, 'm9', 4], [8, 'maj7', 4], [7, 'm7', 4]],                         // i–iv–VI–v (no leading tone)
      s3: [[5, 'm9', 4], [7, 'm7', 4], [0, 'm9', 4], [0, 'madd9', 4]],                         // iv–v–i
      s4: [[0, 'madd9', 4], [0, 'sadmmaj9', 4], [0, 'm9', 4], [0, 'm6', 4]],                   // line cliché i–i(maj7)–i7–i6
      s5: [[0, 'm9', 4], [10, '6', 4], [8, 'maj7', 4], [7, '7sus4', 4]],                       // lament: i–v/♭7–♭VI–V7sus4
      s6: [[8, 'maj9', 4], [7, 'm7', 4], [5, 'm9', 4], [0, 'madd9', 4]],                       // ♭VI–v–iv–i, falling
      s7: [[0, 'm9', 4], [2, 'm7b5', 4], [8, 'maj7', 4], [7, '7sus4', 2], [7, 'm7', 2]],       // i–iiø–♭VI–v
    },
  },
  turnarounds: { major: [[5, 'm6', 2], [4, '7sus4', 2]], minor: [[5, 'm7', 2], [7, '7sus4', 2]] },   // iv–III7sus4 (→ vi) · iv–V7sus4
  grids: GRIDS,
  fills: {
    R1: { stop: 12 },                                                                          // the band stops, the piano rings
    R2: { roll: [[12, 0.3], [13, 0.35], [14, 0.45], [15, 0.55]] },                             // a brush swell
    R3: { kicks: [14] },
  },
  bVariation: false,
  comps: COMPS,
  keysLevel: 1.4,
  plan,
  voicing: voiceSad,
  bass,
  voices(ac, dry, wet, p, ctx) {
    return {
      ep: createSadKeys(ac, dry, wet, p, ctx),
      bass: createSadSub(ac, dry, ctx),
      kit: createHushKit(ac, dry, wet, p.drums.kit, ctx),
      lead: p.lead ? createMuteHorn(ac, dry, wet, p, ctx) : undefined,
    };
  },
};
